import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  FileUp,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  useCreateProposal,
  useParseWordIndex,
  useSuggestStructure,
  type ProposalType,
  type SectionType,
  type SuggestedSection,
  type TemplateSectionConfig,
} from "@/hooks/use-proposals";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// Mejora "Sugerir estructura" (changelog cliente): diálogo que pide
// al backend una estructura de secciones sugerida a partir de los
// documentos 'bases' de la licitación, deja al usuario revisarla y
// crea la propuesta con esas secciones.
//
// Mejora "Subsecciones de primera clase": cada sección puede tener
// subsecciones editables (1 nivel de anidamiento). El usuario las
// añade/quita/reordena igual que las secciones raíz.

/**
 * De dónde sale la estructura inicial:
 * - `ai`: se pide al backend a partir del pliego (comportamiento original).
 * - `file`: el usuario sube un .docx y se extrae su índice.
 *
 * Todo lo demás (editar el árbol, crear la propuesta, extraer requisitos) es
 * idéntico, así que se comparte el mismo diálogo en lugar de duplicarlo.
 */
export type StructureSource = "ai" | "file";

interface SuggestStructureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  licitacionId: string;
  proposalType: ProposalType;
  onCreated: (proposalId: string) => void;
  source?: StructureSource;
}

interface DraftSection {
  localId: string;
  title: string;
  section_type: SectionType;
  rationale?: string | null;
  subsections: DraftSection[];
}

function newLocalId() {
  return `s_${Math.random().toString(36).slice(2, 9)}`;
}

function suggestionToDraft(s: SuggestedSection): DraftSection {
  return {
    localId: newLocalId(),
    title: s.title,
    section_type: s.section_type as SectionType,
    rationale: s.rationale,
    subsections: (s.subsections ?? []).map(suggestionToDraft),
  };
}

export function SuggestStructureDialog({
  isOpen,
  onClose,
  licitacionId,
  proposalType,
  onCreated,
  source = "ai",
}: SuggestStructureDialogProps) {
  const suggest = useSuggestStructure();
  const parseIndex = useParseWordIndex();
  const create = useCreateProposal();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isFileMode = source === "file";
  // Feature C: tras crear, extraemos los requisitos del pliego y los asignamos
  // a las secciones. `finalizing` cubre esa fase (la propuesta ya está creada).
  const [finalizing, setFinalizing] = useState(false);
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const suggestMutate = suggest.mutate;
  const suggestReset = suggest.reset;

  useEffect(() => {
    if (!isOpen) return;
    setSections([]);
    setNotice(null);
    setCollapsed({});
    setTitle(`Propuesta ${proposalType}`);
    suggestReset();
    // En modo fichero no hay nada que pedir al abrir: se espera a que el
    // usuario suba el Word.
    if (isFileMode) return;
    suggestMutate(
      { licitacion_id: licitacionId, proposal_type: proposalType },
      {
        onSuccess: (data) => {
          setNotice(data.notice ?? null);
          setSections(data.sections.map(suggestionToDraft));
        },
        onError: () => {
          showToast.error("No se pudo generar la sugerencia. Intenta de nuevo.");
        },
      }
    );
  }, [isOpen, licitacionId, proposalType, suggestMutate, suggestReset, isFileMode]);

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Se limpia el input para poder volver a elegir el MISMO fichero si el
    // usuario corrige el Word y lo reintenta.
    event.target.value = "";
    if (!file) return;

    setNotice(null);
    parseIndex.mutate(file, {
      onSuccess: (data) => {
        setNotice(data.notice ?? null);
        setSections(data.sections.map(suggestionToDraft));
        if (data.sections.length > 0) {
          const total =
            data.sections.length +
            data.sections.reduce((acc, s) => acc + (s.subsections?.length ?? 0), 0);
          showToast.success(`Índice importado: ${total} secciones detectadas`);
        }
        // El nombre del fichero suele describir la propuesta mejor que el
        // título por defecto.
        const base = file.name.replace(/\.docx$/i, "").trim();
        if (base) setTitle(base);
      },
    });
  };

  if (!isOpen) return null;

  // Helpers que operan sobre el árbol. `parentId === null` apunta a
  // las raíces; un valor concreto apunta a las subsecciones de esa
  // sección.

  const updateSection = (
    localId: string,
    patch: Partial<DraftSection>,
    parentId: string | null = null
  ) => {
    setSections((prev) => {
      if (parentId === null) {
        return prev.map((s) =>
          s.localId === localId ? { ...s, ...patch } : s
        );
      }
      return prev.map((s) =>
        s.localId === parentId
          ? {
              ...s,
              subsections: s.subsections.map((c) =>
                c.localId === localId ? { ...c, ...patch } : c
              ),
            }
          : s
      );
    });
  };

  const move = (localId: string, dir: -1 | 1, parentId: string | null = null) => {
    setSections((prev) => {
      const list = parentId === null
        ? prev
        : prev.find((s) => s.localId === parentId)?.subsections ?? [];
      const idx = list.findIndex((s) => s.localId === localId);
      if (idx === -1) return prev;
      const target = idx + dir;
      if (target < 0 || target >= list.length) return prev;
      const reordered = [...list];
      [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
      if (parentId === null) return reordered;
      return prev.map((s) =>
        s.localId === parentId ? { ...s, subsections: reordered } : s
      );
    });
  };

  const removeSection = (localId: string, parentId: string | null = null) => {
    setSections((prev) => {
      if (parentId === null) {
        return prev.filter((s) => s.localId !== localId);
      }
      return prev.map((s) =>
        s.localId === parentId
          ? {
              ...s,
              subsections: s.subsections.filter((c) => c.localId !== localId),
            }
          : s
      );
    });
  };

  const addSection = () =>
    setSections((prev) => [
      ...prev,
      {
        localId: newLocalId(),
        title: "",
        section_type: "CUSTOM" as SectionType,
        subsections: [],
      },
    ]);

  const addSubsection = (parentId: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s.localId === parentId
          ? {
              ...s,
              subsections: [
                ...s.subsections,
                {
                  localId: newLocalId(),
                  title: "",
                  section_type: "CUSTOM" as SectionType,
                  subsections: [],
                },
              ],
            }
          : s
      )
    );
    // Si el usuario añade un hijo, asegurar que la rama está expandida.
    setCollapsed((c) => ({ ...c, [parentId]: false }));
  };

  const toggleCollapsed = (id: string) =>
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const handleCreate = () => {
    const toConfig = (
      list: DraftSection[]
    ): TemplateSectionConfig[] =>
      list
        .map((s, idx) => ({
          ...s,
          title: s.title.trim(),
          section_order: idx,
        }))
        .filter((s) => s.title.length > 0)
        .map((s) => ({
          title: s.title,
          section_type: s.section_type,
          section_order: s.section_order,
          subsections: toConfig(s.subsections),
        }));

    const sections_config = toConfig(sections);

    if (sections_config.length === 0) {
      showToast.error("Añade al menos una sección antes de crear la propuesta");
      return;
    }

    create.mutate(
      {
        licitacion_id: licitacionId,
        proposal_type: proposalType,
        title: title.trim() || undefined,
        sections_config,
      },
      {
        onSuccess: async (data) => {
          // Feature C ("auto al sugerir estructura"): extraemos los requisitos
          // del pliego y los asignamos a las secciones recién creadas. Es
          // best-effort: si falla, la propuesta se crea igual y el usuario
          // puede pulsar "Regenerar" desde el editor.
          setFinalizing(true);
          try {
            await fetchWithAuth(
              `/api/v1/proposals/${data.id}/requirements/extract`,
              { method: "POST", body: JSON.stringify({}) }
            );
          } catch {
            // silencioso: no bloquea la creación
          } finally {
            setFinalizing(false);
            onCreated(data.id);
            onClose();
          }
        },
      }
    );
  };

  const isLoadingSuggestion = suggest.isPending || parseIndex.isPending;

  // Render de una sub-fila (más compacta, indentada).
  const renderSub = (sub: DraftSection, parentId: string, idx: number, total: number) => (
    <li
      key={sub.localId}
      className="border border-gray-200 rounded p-2 bg-gray-50/60"
    >
      <div className="flex items-start gap-2">
        <span className="text-[10px] font-mono text-gray-400 mt-1.5">
          {idx + 1}
        </span>
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={sub.title}
            onChange={(e) =>
              updateSection(sub.localId, { title: e.target.value }, parentId)
            }
            placeholder="Título del apartado"
            className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
          />
          {sub.rationale && (
            <p className="text-[11px] text-gray-500 mt-1 italic">
              {sub.rationale}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => move(sub.localId, -1, parentId)}
            disabled={idx === 0}
            className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
            title="Subir"
          >
            <ArrowUp className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => move(sub.localId, 1, parentId)}
            disabled={idx === total - 1}
            className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-30"
            title="Bajar"
          >
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => removeSection(sub.localId, parentId)}
          className="p-1 text-gray-400 hover:text-red-600"
          title="Eliminar apartado"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </li>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={
        isFileMode ? "Importar índice desde Word" : "Sugerir estructura desde el pliego"
      }
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            {isFileMode ? (
              <FileUp className="w-5 h-5 text-blue-600" />
            ) : (
              <Sparkles className="w-5 h-5 text-purple-600" />
            )}
            <h2 className="text-lg font-semibold text-gray-900">
              {isFileMode
                ? "Importar índice desde Word"
                : "Estructura sugerida desde el pliego"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {isFileMode ? (
            <p className="text-sm text-gray-500">
              Sube el Word con el índice que exige el pliego. Se detectan las
              secciones por su numeración (<code>1.</code>, <code>1.1.</code>…) o
              por los estilos de título. Revísalas y pulsa{" "}
              <em>Crear propuesta</em>.
            </p>
          ) : (
            <p className="text-sm text-gray-500">
              La IA ha analizado los documentos de la sección{" "}
              <strong>Bases de la convocatoria</strong> y propone esta estructura
              con sus apartados. Revísala, edítala como prefieras y pulsa{" "}
              <em>Crear propuesta</em>.
            </p>
          )}

          {isFileMode && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleFileSelected}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={parseIndex.isPending || create.isPending}
                className="inline-flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50"
              >
                <FileUp className="w-4 h-4" />
                {sections.length > 0
                  ? "Elegir otro documento"
                  : "Seleccionar documento Word (.docx)"}
              </button>
            </div>
          )}

          {isLoadingSuggestion ? (
            <div className="flex flex-col items-center justify-center py-12 text-sm text-gray-500">
              <Loader2 className="w-6 h-6 text-purple-500 animate-spin mb-2" />
              {isFileMode ? "Leyendo el índice…" : "Analizando el pliego…"}
            </div>
          ) : (
            <>
              {notice && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                  {notice}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título de la propuesta
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {sections.length === 0 && !notice && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
                  {isFileMode
                    ? "Selecciona un documento Word para importar su índice, o añade las secciones a mano."
                    : "No se sugirió ninguna sección. Añade manualmente las que necesites."}
                </div>
              )}

              <ul className="space-y-2">
                {sections.map((s, idx) => {
                  const hasSubs = s.subsections.length > 0;
                  const isCollapsed = collapsed[s.localId];
                  return (
                    <li
                      key={s.localId}
                      className="border border-gray-200 rounded-md p-3 bg-white"
                    >
                      <div className="flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => toggleCollapsed(s.localId)}
                          className="mt-1.5 text-gray-400 hover:text-gray-700"
                          title={isCollapsed ? "Expandir" : "Contraer"}
                          aria-label={
                            isCollapsed ? "Expandir apartados" : "Contraer apartados"
                          }
                        >
                          {isCollapsed ? (
                            <ChevronRight className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        <span className="text-xs font-mono text-gray-400 mt-2">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={s.title}
                            onChange={(e) =>
                              updateSection(s.localId, { title: e.target.value })
                            }
                            placeholder="Título de la sección"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                          {s.rationale && (
                            <p className="text-xs text-gray-500 mt-1 italic">
                              {s.rationale}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => move(s.localId, -1)}
                            disabled={idx === 0}
                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                            title="Subir"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => move(s.localId, 1)}
                            disabled={idx === sections.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                            title="Bajar"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSection(s.localId)}
                          className="p-1.5 text-gray-400 hover:text-red-600"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {!isCollapsed && (
                        <div className="mt-2 ml-6 pl-3 border-l-2 border-purple-100">
                          {hasSubs && (
                            <ul className="space-y-1.5">
                              {s.subsections.map((sub, subIdx) =>
                                renderSub(sub, s.localId, subIdx, s.subsections.length)
                              )}
                            </ul>
                          )}
                          <button
                            type="button"
                            onClick={() => addSubsection(s.localId)}
                            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 mt-2"
                          >
                            <Plus className="w-3 h-3" />
                            Añadir apartado
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              <button
                type="button"
                onClick={addSection}
                className="inline-flex items-center gap-1 text-sm text-purple-700 hover:text-purple-900"
              >
                <Plus className="w-4 h-4" />
                Añadir sección
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={
              isLoadingSuggestion ||
              create.isPending ||
              finalizing ||
              sections.length === 0
            }
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            {create.isPending || finalizing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {finalizing ? "Extrayendo requisitos…" : "Crear propuesta"}
          </button>
        </div>
      </div>
    </div>
  );
}
